import { useState } from 'react';
import { Check, Copy, Ticket, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { api } from '../../lib/api';
import { usePermission } from '../rbac/use-permission';
import { P } from '../../lib/rbac/permissions.generated';

type Props = Readonly<{
  /** Das Konto, für das der Code gilt — ein Einmalcode gehört genau einem. */
  wpUserId: number;
  code: string;
  onCode: (code: string) => void;
}>;

/**
 * Einmalcode für diese Anmeldung erzeugen (T-0011, Schritt „Pakete").
 *
 * Der Code wird auf das Konto des Kunden ausgestellt und lässt sich genau
 * einmal einlösen. Ein versehentlich falscher Code lässt sich im selben Fenster
 * wieder entfernen — er wird dann auch in der Gutscheinverwaltung gelöscht,
 * sonst zählte er weiter gegen das Monatslimit.
 */
export default function EinmalcodeFeld({ wpUserId, code, onCode }: Props) {
  // Eigenes Recht der Anmeldung — das Büro darf Codes für genau diesen Kunden
  // ausstellen, mit Höchstbetrag je Code (Entscheidung vom 27.09.2026).
  const darfErzeugen = usePermission(P.anmeldung.einmalcode_ausstellen);
  const [betrag, setBetrag] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  // Nur für einen hier erzeugten Code bekannt; ein übernommener wird nur abgelöst.
  const [gutscheinId, setGutscheinId] = useState<number | null>(null);

  const erzeugen = async () => {
    const wert = Number(String(betrag).replace(',', '.'));
    if (!Number.isFinite(wert) || wert <= 0) {
      toast.error('Bitte einen Betrag in Euro angeben');
      return;
    }
    if (wpUserId <= 0) {
      toast.error('Ohne Kundenkonto lässt sich kein persönlicher Code ausstellen');
      return;
    }
    setLaeuft(true);
    try {
      const erzeugt = await api.post<{ id: number; code: string }>('/api/enrollments/einmalcode', {
        wpUserId,
        valueEuro: wert,
      });
      setGutscheinId(Number(erzeugt.id) || null);
      onCode(erzeugt.code);
      toast.success(`Einmalcode ${erzeugt.code} erstellt`);
    } catch (e: any) {
      toast.error(e?.message || 'Der Code konnte nicht erstellt werden');
    } finally {
      setLaeuft(false);
    }
  };

  const entfernen = async () => {
    setLaeuft(true);
    try {
      if (gutscheinId) {
        try {
          await api.delete(`/api/enrollments/einmalcode/${gutscheinId}`);
        } catch {
          toast.warning('Der Code ist aus der Anmeldung entfernt, bleibt aber in der Gutscheinverwaltung stehen.');
        }
      }
      setGutscheinId(null);
      setBetrag('');
      onCode('');
      toast.success('Einmalcode entfernt');
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="grid gap-3 rounded-lg border p-4">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Ticket className="h-4 w-4 text-muted-foreground" aria-hidden /> Einmalcode
      </div>
      <p className="text-xs text-muted-foreground">
        Optional: ein Rabattcode, den nur dieser Kunde und nur einmal einlösen kann.
        Er gilt für die erste Zahlung, hat einen Höchstbetrag je Code und steht als {'{{gutscheinCode}}'} in der Angebotsmail.
      </p>
      {code ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-primary/40 bg-primary/5 px-3 py-2">
          <span className="font-mono text-sm tracking-wider">{code}</span>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                void navigator.clipboard?.writeText(code);
                toast.success('Code kopiert');
              }}
            >
              <Copy className="mr-1 h-4 w-4" aria-hidden /> Kopieren
            </Button>
            <Button type="button" variant="ghost" size="sm" disabled={laeuft} onClick={() => void entfernen()}>
              <X className="mr-1 h-4 w-4" aria-hidden /> Entfernen
            </Button>
          </div>
        </div>
      ) : darfErzeugen ? (
        <div className="flex items-end gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="einmalcode-betrag">Wert in Euro</Label>
            <Input
              id="einmalcode-betrag"
              className="w-32"
              inputMode="decimal"
              placeholder="20,00"
              value={betrag}
              onChange={(e) => setBetrag(e.target.value)}
            />
          </div>
          <Button type="button" variant="outline" disabled={laeuft} onClick={() => void erzeugen()}>
            <Check className="mr-1 h-4 w-4" aria-hidden />
            {laeuft ? 'Wird erstellt…' : 'Code erzeugen'}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Für einen Einmalcode fehlt Dir das Recht „Einmalcode ausstellen“.
        </p>
      )}
    </div>
  );
}
